import { Form } from 'react-bootstrap';
import { type FormikProps } from 'formik';
  import { memo, useState } from 'react';
import NeatixModal from '@components/Modal/NeatixModal';
import PopoverCustom from '@components/Shared/Popover/PopoverCustom';
import { type UnApplyPrepaymentsModels } from '@store/Finance/AR/ArReceipts/ArReceiptsModels';
import { Card, CardBody, CardHeader } from '@components/card/card';

interface Props {
  formik: FormikProps<any>;
  UnApplyPrepaymentsList: UnApplyPrepaymentsModels[];
}
 

const ArReceiptSchedules = ({ formik, UnApplyPrepaymentsList }: Props) => {
  const { values, setFieldValue } = formik;
  const { arTransactionLinesList, arReceiptSchedulesList, transactionType, postedFlag } = values;

  const [showModal, setShowModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const totalInvoiceLines = arTransactionLinesList?.reduce((sum: number, item: any) => sum + item.total, 0);
  const totalReceiptSchedules = arReceiptSchedulesList?.reduce(
    (sum: number, payment: any) => sum + Number(payment.amount),
    0
  );
  const remainingAmount = Number(totalInvoiceLines) - Number(totalReceiptSchedules);

  const handlePaymentsChange = (index: number, field: string, value: any) => {
    const updatedReceipts = [...arReceiptSchedulesList];
    const receipt = updatedReceipts[index];

    updatedReceipts[index] = {
      ...receipt,
      [field]: value
    };

    if (field === 'amount') {
      const numericValue = value === '' || value === null ? 0 : Number(value);
      updatedReceipts[index][field] = !isNaN(numericValue) ? numericValue : 0;

      // التأكد من أن المبلغ لا يتجاوز المبلغ المتبقي للدفعات المقدمة
      if (receipt.paymentId !== null && numericValue > receipt.PaymentAmountUnApply) {
        updatedReceipts[index][field] = receipt.PaymentAmountUnApply;
      }
    }

    formik?.setFieldValue('arReceiptSchedulesList', updatedReceipts);
  };

  const handleAddPayment = () => {
    const newReceipt = {
      id: null,
      amount: remainingAmount > 0 ? remainingAmount : 0,
      dueDate: new Date().toISOString().split('T')[0],
      paymentId: null
    };
    setFieldValue('arReceiptSchedulesList', [...arReceiptSchedulesList, newReceipt]);
  };

  const confirmDelete = (index: number) => {
    setDeleteIndex(index);
    setShowDeleteConfirm(true);
  };

  const handleDeletePayment = () => {
    if (deleteIndex !== null) {
      const updatedReceipts = arReceiptSchedulesList?.filter(
        (_: any, receiptIndex: any) => receiptIndex !== deleteIndex
      );
      setFieldValue('arReceiptSchedulesList', updatedReceipts);
    }
    setShowDeleteConfirm(false);
    setDeleteIndex(null);
  };

  const handleSelectPrepayment = (item: any) => {
    const selectedAmount = item.amountUnApply > remainingAmount ? remainingAmount : item.amountUnApply;

    const prepayment = {
      id: null,
      amount: selectedAmount,
      dueDate: new Date().toISOString().split('T')[0],
      paymentId: item.id,
      PaymentAmountUnApply: item.amountUnApply,
      paymentCode: item.code,
      paymentDate: new Date(item.depositDate).toLocaleDateString(),
      paymentAmount: item.amount
    };

    setFieldValue('arReceiptSchedulesList', [...arReceiptSchedulesList, prepayment]);
    setShowModal(false);
  };

  return (
    <>
      <div className='row'>
        <div className='col-lg-4 col-md-5'>
          <Card className='card mb-4'>
            <CardHeader>
              <h6 className='mb-0'>ملخص الدفعات</h6>
            </CardHeader>
            <CardBody>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <span className='fw-medium'>إجمالي الفاتورة:</span>
                <span className='fw-bold text-primary'>{totalInvoiceLines?.toFixed(2) || '0.00'}</span>
              </div>

              <div className='d-flex justify-content-between align-items-center mb-3'>
                <span className='fw-medium'>إجمالي الجدولة:</span>
                <span className='fw-bold text-success'>{totalReceiptSchedules?.toFixed(2) || '0.00'}</span>
              </div>

              <div className='d-flex justify-content-between align-items-center mb-3' hidden={postedFlag}>
                <span className='fw-medium'>المتبقي:</span>
                <span className={`fw-bold ${remainingAmount < 0 ? 'text-danger' : 'text-warning'}`}>
                  {remainingAmount?.toFixed(2) || '0.00'}
                </span>
              </div>

              <div className='row g-2 mt-3' hidden={postedFlag}>
                <div className='col-12'>
                  <button
                    disabled={remainingAmount <= 0}
                    type='button'
                    className='btn btn-primary w-100 d-flex align-items-center justify-content-center'
                    onClick={handleAddPayment}
                  >
                    <i className='fas fa-money-bill me-2'></i>
                    إضافة دفعة جديدة
                  </button>
                </div>
               
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h6 className='mb-0'>معلومات مهمة</h6>
            </CardHeader>
            <CardBody>
              <div className='alert alert-info py-2'>
                <small>
                  <i className='fas fa-info-circle me-2'></i>
                  يمكنك إضافة دفعات جديدة طالما هناك مبلغ متبقي
                </small>
              </div>
              <div className='alert alert-warning py-2'>
                <small>
                  <i className='fas fa-exclamation-triangle me-2'></i>
                  المبلغ السلبي يعني أن مجموع الدفعات أكبر من إجمالي الفاتورة
                </small>
              </div>
            </CardBody>
          </Card>
        </div>

        <div className='col-lg-8 col-md-7'>
          <Card>
            <CardHeader className='d-flex justify-content-between align-items-center'>
              <h6 className='mb-0'>جدول الدفعات</h6>
              <span className='badge bg-theme'>{arReceiptSchedulesList?.length || 0} دفعة</span>
            </CardHeader>
            <CardBody className='p-0'>
              <div className='table-responsive'>
                <table className='table table-hover mb-0'>
                  <thead className='table-active fw-bold shadow'>
                    <tr>
                      <th className='text-center small'>#</th>
                      <th className='text-center small'>رقم الدفعة</th>
                      <th className='text-center small'>المبلغ</th>
                      <th className='text-center small'>تاريخ الاستحقاق</th>
                      <th className='text-center small' hidden={postedFlag}>
                        الإجراءات
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {arReceiptSchedulesList?.length > 0 ? (
                      arReceiptSchedulesList.map((line: any, index: any) => (
                        <tr key={index} className={line.paymentId !== null ? 'table-lightzz' : ''}>
                          <td className='text-center align-middle'>{index + 1}</td>
                          <td className='text-center align-middle'>
                            <PopoverCustom
                              childern={
                                <div className='d-flex align-items-center justify-content-center'>
                                  <Form.Control
                                    className='text-center border-0 bg-transparent'
                                    type='text'
                                    value={
                                      line.paymentId !== null
                                        ? `${line.paymentCode || ''} (مقدمة)`
                                        : line.paymentCode || 'دفعة عادية'
                                    }
                                    readOnly
                                  />
                                  {line.paymentId !== null && <i className='fas fa-certificate text-warning ms-2'></i>}
                                </div>
                              }
                              Header={'تفاصيل الدفعة'}
                              Body={
                                <div className='p-2'>
                                  {line.paymentId !== null ? (
                                    <>
                                      <div className='d-flex justify-content-between mb-2'>
                                        <span>رقم الدفعة:</span>
                                        <strong>{line.paymentCode}</strong>
                                      </div>
                                      <div className='d-flex justify-content-between mb-2'>
                                        <span>تاريخ الدفعة:</span>
                                        <strong>{line.paymentDate}</strong>
                                      </div>
                                      <div className='d-flex justify-content-between mb-2'>
                                        <span>مبلغ الدفعة الأصلي:</span>
                                        <strong className='text-success'>
                                          {Number(line.paymentAmount).toFixed(2)}
                                        </strong>
                                      </div>
                                      <div className='d-flex justify-content-between mb-2'>
                                        <span>المبلغ المتبقي:</span>
                                        <strong className='text-primary'>
                                          {Number(line.PaymentAmountUnApply).toFixed(2)}
                                        </strong>
                                      </div>
                                    </>
                                  ) : (
                                    <div className='text-center py-2'>
                                      <i className='fas fa-money-bill-wave fa-2x text-success mb-2'></i>
                                      <p>دفعة عادية - سيتم استلامها وفقًا لشروط السداد</p>
                                    </div>
                                  )}
                                </div>
                              }
                            />
                          </td>
                          <td className='text-center align-middle'>
                            <Form.Control
                              disabled={postedFlag}
                              className='text-center'
                              type='number'
                              min='0'
                              step='0.01'
                              value={Number(line.amount) || ''}
                              onChange={e => handlePaymentsChange(index, 'amount', parseFloat(e.target.value))}
                              onBlur={e => {
                                let value = parseFloat(e.target.value);
                                if (isNaN(value)) value = 0;

                                // التحقق من القيمة بالنسبة للدفعات المقدمة
                                if (line.paymentId !== null && value > line.PaymentAmountUnApply) {
                                  value = line.PaymentAmountUnApply;
                                }

                                // التحقق من عدم تجاوز المبلغ المتبقي
                                if (value > remainingAmount + line.amount) {
                                  value = remainingAmount + line.amount;
                                }

                                handlePaymentsChange(index, 'amount', value);
                              }}
                            />
                          </td>
                          <td className='text-center align-middle'>
                            <Form.Control
                              disabled={postedFlag}
                              className='text-center'
                              type='date'
                              value={line.dueDate || ''}
                              onChange={e => handlePaymentsChange(index, 'dueDate', e.target.value)}
                            />
                          </td>
                          <td className='text-center align-middle' hidden={postedFlag}>
                            <button
                              type='button'
                              className='btn btn-sm btn-outline-danger'
                              onClick={() => confirmDelete(index)}
                            >
                              <i className='fas fa-trash-alt'></i>
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className='text-center py-4'>
                          <i className='fas fa-receipt fa-2x text-muted mb-2'></i>
                          <p className='text-muted'>لا توجد دفعات مضافة حتى الآن</p>
                          <button
                            type='button'
                            className='btn btn-sm btn-primary'
                            onClick={handleAddPayment}
                            disabled={remainingAmount <= 0}
                          >
                            إضافة أول دفعة
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

   

      {/* تأكيد الحذف */}
      <NeatixModal show={showDeleteConfirm} setShow={setShowDeleteConfirm} size='md' centered title='تأكيد الحذف'>
        <div className='text-center py-4'>
          <i className='fas fa-exclamation-triangle fa-2x text-warning mb-3'></i>
          <h5>هل أنت متأكد من حذف هذه الدفعة؟</h5>
          <p className='text-muted'>لا يمكن التراجع عن هذه العملية بعد التنفيذ</p>
          <div className='d-flex justify-content-center mt-4'>
            <button className='btn btn-outline-secondary me-3' onClick={() => setShowDeleteConfirm(false)}>
              إلغاء
            </button>
            <button className='btn btn-danger' onClick={handleDeletePayment}>
              نعم، احذف الدفعة
            </button>
          </div>
        </div>
      </NeatixModal>
    </>
  );
};

export default memo(ArReceiptSchedules);
